import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip,
} from '@mui/material';
import styled from 'styled-components';
import { Phone, Verified, Send, Check, Star } from '@mui/icons-material';
import { userService } from '../../services/api/user.service';
import { useAuthStore } from '../../stores/auth.store';

const StyledContainer = styled(Box)`
  background: linear-gradient(
    135deg,
    rgba(109, 83, 159, 0.05) 0%,
    rgba(59, 130, 246, 0.05) 100%
  );
  border: 1px solid rgba(109, 83, 159, 0.15);
  border-radius: 16px;
  padding: 1rem;
  margin-bottom: 1rem;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 600px) {
    padding: 2rem;
    margin-bottom: 2rem;
    gap: ${(props) => props.theme.spacing?.lg || '24px'};
  }
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 1rem;
  width: 100%;

  .MuiOutlinedInput-root {
    background-color: ${(props) =>
      props.theme.colors?.input?.background || '#1a1a24'};
    border-radius: 12px;

    fieldset {
      border-color: ${(props) => props.theme.colors?.input?.border || '#333'};
    }

    &:hover fieldset {
      border-color: ${(props) => props.theme.colors?.primary || '#007bff'};
    }

    &.Mui-focused fieldset {
      border-color: ${(props) => props.theme.colors?.primary || '#007bff'};
      border-width: 2px;
    }
  }

  .MuiInputLabel-root {
    color: ${(props) => props.theme.colors?.text?.secondary || '#999'};

    &.Mui-focused {
      color: ${(props) => props.theme.colors?.primary || '#007bff'};
    }
  }

  .MuiInputBase-input {
    color: ${(props) => props.theme.colors?.text?.primary || '#fff'};
  }

  @media (min-width: 600px) {
    margin-bottom: ${(props) => props.theme.spacing?.md || '16px'};
  }

  .MuiFormHelperText-root {
    color: ${(props) => props.theme.colors?.text?.secondary || '#999'};
  }
`;

const PrimaryButton = styled(Button)`
  background: ${(props) =>
    props.theme.gradients?.primary ||
    'linear-gradient(135deg, #6D539F 0%, #3B82F6 100%)'};
  color: ${(props) => props.theme.colors?.white || '#fff'};
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  min-height: 48px;

  &:hover {
    background: ${(props) =>
      props.theme.gradients?.primary ||
      'linear-gradient(135deg, #6D539F 0%, #3B82F6 100%)'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px
      ${(props) => props.theme.colors?.primary || '#007bff'}40;
  }

  &:disabled {
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled(Button)`
  border: 2px solid ${(props) => props.theme.colors?.input?.border || '#333'};
  color: ${(props) => props.theme.colors?.text?.primary || '#fff'};
  background: transparent;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  min-height: 48px;

  &:hover {
    border-color: ${(props) => props.theme.colors?.primary || '#007bff'};
    background: ${(props) => props.theme.colors?.primary || '#007bff'}10;
    transform: translateY(-1px);
  }
`;

interface PhoneVerificationProps {
  currentPhone?: string;
  isVerified?: boolean;
  onVerificationComplete?: (phone: string) => void;
  onUnverify?: () => void;
}

export const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  currentPhone = '',
  isVerified = false,
  onVerificationComplete,
  onUnverify,
}) => {
  const [phone, setPhone] = useState(currentPhone);
  const [otpCode, setOtpCode] = useState('');
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { fetchProfile, profile } = useAuthStore();

  // Check if user has a pending verification code on mount
  React.useEffect(() => {
    const hasPendingCode =
      profile?.settings?.verifyMobileData?.verifyMobileCode;
    const hasUnverifiedMobile =
      profile?.settings?.verifyMobileData?.unverifiedMobile;

    // If there's a pending code and unverified mobile, show OTP step
    if (hasPendingCode && hasUnverifiedMobile && !isVerified) {
      setStep('otp');
      setPhone(currentPhone); // Use the phone from parent (could be unverifiedMobile)
    }
  }, [profile, isVerified, currentPhone]);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (digits.length >= 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return digits;
  };

  const handlePhoneChange = (event: any) => {
    const formatted = formatPhoneNumber(event.target.value);
    setPhone(formatted);
    setError(null);
  };

  const handleSendOTP = useCallback(async () => {
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cleanPhone = phone.replace(/\D/g, '');
      // Only add +1 if the number doesn't already start with 1 (US country code)
      const formattedPhone = cleanPhone.startsWith('1')
        ? `+${cleanPhone}`
        : `+1${cleanPhone}`;

      const response = await userService.generateOTP({
        mobile: formattedPhone,
      });

      if (response.success) {
        setStep('otp');
        setSuccess('Verification code sent to your phone');
      } else {
        setError(response.message || 'Failed to send verification code');
      }
    } catch (_err) {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [phone]);

  const handleVerifyOTP = useCallback(async () => {
    if (!otpCode || otpCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await userService.verifyOTP({ otpCode });

      if (response.success) {
        setSuccess('Phone number verified successfully!');
        setStep('phone');
        setOtpCode('');

        // Refresh profile to get updated verification status
        await fetchProfile();

        if (onVerificationComplete) {
          onVerificationComplete(phone);
        }
      } else {
        setError(response.message || 'Invalid verification code');
      }
    } catch (_err) {
      setError('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [otpCode, phone, onVerificationComplete]); // Removed fetchProfile to prevent infinite loop

  const handleUnverify = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await userService.unverifyPhone();

      if (response.success) {
        setSuccess('Phone number unverified');
        setPhone('');

        // Refresh profile
        await fetchProfile();

        if (onUnverify) {
          onUnverify();
        }
      } else {
        setError(response.message || 'Failed to unverify phone number');
      }
    } catch (_err) {
      setError('Failed to unverify phone number. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onUnverify]); // Removed fetchProfile to prevent infinite loop

  const handleResendOTP = () => {
    setOtpCode('');
    setError(null);
    setSuccess(null);
    handleSendOTP();
  };

  if (isVerified && currentPhone && step === 'phone') {
    return (
      <StyledContainer>
        <Typography variant="h6" color="primary">
          Phone Number
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Chip
            icon={<Verified />}
            label={`Verified: ${currentPhone}`}
            variant="outlined"
            sx={{
              borderColor: '#1DA1F2',
              borderRadius: '12px',
              fontWeight: 500,
              '& .MuiChip-icon': {
                color: '#1DA1F2',
              },
            }}
          />
          <SecondaryButton
            variant="outlined"
            size="small"
            onClick={handleUnverify}
            disabled={loading}
          >
            Unverify
          </SecondaryButton>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <Box
          sx={{
            fontSize: '1.75rem',
            fontWeight: 600,
            color: '#F0F3FF',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            margin: 0,
          }}
        >
          <Star sx={{ fontSize: 28, color: '#E4CB76' }} />
          Phone Verification
          {isVerified && (
            <Box
              sx={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#F0F3FF',
                padding: '0.375rem 0.875rem',
                borderRadius: '24px',
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Verified sx={{ fontSize: 16 }} />
              Verified
            </Box>
          )}
        </Box>
      </Box>

      {/* Content Section */}
      {isVerified ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            padding: '1.5rem',
            background:
              'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '12px',
            color: '#F0F3FF',
          }}
        >
          <Verified sx={{ fontSize: 24, color: '#10B981' }} />
          <Box>
            <Typography
              variant="body1"
              sx={{ color: '#F0F3FF', fontWeight: 600 }}
            >
              Phone number verified
            </Typography>
            <Typography variant="body2" sx={{ color: '#9C9C9D' }}>
              {currentPhone}
            </Typography>
          </Box>
          {onUnverify && (
            <Button
              onClick={onUnverify}
              sx={{
                marginLeft: 'auto',
                color: '#9C9C9D',
                textTransform: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  color: '#F0F3FF',
                },
              }}
            >
              Change
            </Button>
          )}
        </Box>
      ) : step === 'phone' ? (
        <>
          <Typography
            variant="body2"
            sx={{ color: '#9C9C9D', marginBottom: '1rem' }}
          >
            Secure your account by verifying your phone number
          </Typography>

          <StyledTextField
            fullWidth
            label="Phone Number"
            value={phone}
            onChange={handlePhoneChange}
            placeholder="(555) 123-4567"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone color="action" />
                </InputAdornment>
              ),
            }}
            helperText="Enter your 10-digit US phone number"
          />

          <PrimaryButton
            variant="contained"
            onClick={handleSendOTP}
            disabled={loading || !phone || phone.replace(/\D/g, '').length < 10}
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
            sx={{
              alignSelf: 'flex-start',
              minWidth: { xs: '100%', sm: '200px' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {loading ? 'Sending...' : 'Send Verification Code'}
          </PrimaryButton>
        </>
      ) : (
        <>
          <Typography
            variant="body2"
            sx={{ color: '#9C9C9D', marginBottom: '1rem' }}
          >
            Enter the 6-digit code sent to {phone}
          </Typography>

          <StyledTextField
            fullWidth
            label="Verification Code"
            value={otpCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setOtpCode(value);
              setError(null);
            }}
            placeholder="123456"
            inputProps={{
              maxLength: 6,
              style: {
                textAlign: 'center',
                fontSize: '1.2rem',
                letterSpacing: '0.5rem',
              },
            }}
          />

          {/* Primary Action */}
          <PrimaryButton
            variant="contained"
            onClick={handleVerifyOTP}
            disabled={loading || otpCode.length !== 6}
            startIcon={loading ? <CircularProgress size={20} /> : <Check />}
            sx={{
              alignSelf: 'flex-start',
              minWidth: { xs: '100%', sm: '200px' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </PrimaryButton>

          {/* Secondary Actions */}
          <Box
            sx={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
              marginTop: '0.5rem',
            }}
          >
            <Button
              onClick={handleResendOTP}
              disabled={loading}
              sx={{
                color: '#9C9C9D',
                textTransform: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  color: '#F0F3FF',
                },
              }}
            >
              Resend Code
            </Button>
            <Button
              onClick={() => {
                setStep('phone');
                setOtpCode('');
                setError(null);
                setSuccess(null);
              }}
              disabled={loading}
              sx={{
                color: '#9C9C9D',
                textTransform: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  color: '#F0F3FF',
                },
              }}
            >
              Change Number
            </Button>
          </Box>
        </>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            color: '#FCA5A5',
            '& .MuiAlert-icon': {
              color: '#EF4444',
            },
          }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            color: '#6EE7B7',
            '& .MuiAlert-icon': {
              color: '#10B981',
            },
          }}
        >
          {success}
        </Alert>
      )}
    </StyledContainer>
  );
};

export default PhoneVerification;
